import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Analytics, logEvent } from 'firebase/analytics';
import { addDoc, collection, doc, getDoc } from 'firebase/firestore';
import ReactGA from 'react-ga4';
import { analytics as analyticsPromise, db as firestoreDb } from '../lib/firebase';

interface AnalyticsContextType {
  trackEvent: (eventName: string, params?: Record<string, unknown>) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [isGAInitialized, setIsGAInitialized] = useState(false);
  const customEventLoggingDisabledRef = useRef(false);
  const customEventLoggingWarnedRef = useRef(false);

  useEffect(() => {
    analyticsPromise.then(setAnalytics);

    const initGA = async () => {
      try {
        const configDoc = await getDoc(doc(firestoreDb, 'config', 'store'));
        if (!configDoc.exists()) return;

        const config = configDoc.data();
        const gaId = config.integrations?.googleAnalytics;

        if (gaId && gaId.startsWith('G-')) {
          ReactGA.initialize(gaId);
          setIsGAInitialized(true);
          console.warn('GA4 Initialized with ID:', gaId);
        }
      } catch (error) {
        console.error('Error initializing GA4:', error);
      }
    };

    initGA();
  }, []);

  const trackEvent = useCallback(
    async (eventName: string, params?: Record<string, unknown>) => {
      if (isGAInitialized) {
        ReactGA.event({ category: 'User', action: eventName, ...params });
      }

      if (analytics) {
        logEvent(analytics, eventName, params);
      }

      if (customEventLoggingDisabledRef.current) return;

      try {
        const safeParams = params ? JSON.parse(JSON.stringify(params)) : {};

        await addDoc(collection(firestoreDb, 'events'), {
          eventName,
          params: safeParams,
          path: location.pathname,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        });
      } catch (error) {
        const errorCode =
          typeof error === 'object' &&
          error !== null &&
          'code' in error &&
          typeof (error as { code?: unknown }).code === 'string'
            ? (error as { code: string }).code
            : '';

        if (errorCode === 'permission-denied') {
          customEventLoggingDisabledRef.current = true;

          if (!customEventLoggingWarnedRef.current) {
            customEventLoggingWarnedRef.current = true;
            console.warn(
              "Custom Firestore event logging disabled: missing write permission for 'events'."
            );
          }

          return;
        }

        console.error('Error logging event to Firestore:', error);
      }
    },
    [analytics, isGAInitialized, location.pathname]
  );

  useEffect(() => {
    if (isGAInitialized) {
      ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
    }

    if (analytics) {
      logEvent(analytics, 'page_view', {
        page_path: location.pathname + location.search,
      });
    }

    trackEvent('page_view', {
      path: location.pathname + location.search,
    });

    const SESSION_KEY = 'Veraz_last_visit';
    const SESSION_DURATION = 60 * 60 * 1000;

    const checkSession = async () => {
      const now = Date.now();
      const lastVisit = localStorage.getItem(SESSION_KEY);

      if (!lastVisit || now - parseInt(lastVisit, 10) > SESSION_DURATION) {
        try {
          const { db: serviceDb } = await import('../services/backend');
          await serviceDb.trackVisit();
          console.warn('New session recorded:', new Date().toISOString());
        } catch (err) {
          console.error('Failed to record visit:', err);
        }
      }

      localStorage.setItem(SESSION_KEY, now.toString());
    };

    checkSession();
  }, [location, isGAInitialized, analytics, trackEvent]);

  return <AnalyticsContext.Provider value={{ trackEvent }}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
